import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useEffect, useState } from "react";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { Skeleton } from "@components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import { AiContextSnapshotPanel } from "@components/ai/AiContextSnapshotPanel";
import { formatContextSnapshotClipboard } from "@/lib/contextSnapshot";
import {
  createEntity,
  getThreadContextSnapshot,
  fetchThreadSnapshot,
  getThreadDetail,
  proposeWorkItem,
  refreshThreadContextSnapshot,
  type AiContextSnapshot,
  type ThreadDetail,
} from "../lib/auth";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";

type ThreadDetailPageProps = {
  threadId: string;
  navigate: (route: string) => void;
  onNoticeChange: (notice: string | null) => void;
};

export function ThreadDetailPage({
  threadId,
  navigate,
  onNoticeChange,
}: ThreadDetailPageProps) {
  const [threadDetail, setThreadDetail] = useState<ThreadDetail | null>(null);
  const [threadDetailError, setThreadDetailError] = useState<string | null>(null);
  const [isThreadDetailLoading, setIsThreadDetailLoading] = useState(false);
  const [actionItemBusy, setActionItemBusy] = useState<string | null>(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [threadSnapshot, setThreadSnapshot] = useState<string | null>(null);
  const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
  const [contextSnapshot, setContextSnapshot] = useState<AiContextSnapshot | null>(null);
  const [isContextSnapshotLoading, setIsContextSnapshotLoading] = useState(false);
  const [isContextSnapshotRefreshing, setIsContextSnapshotRefreshing] = useState(false);
  const [visibleMessageCount, setVisibleMessageCount] = useState(50);
  const [visibleThreadTimelineCount, setVisibleThreadTimelineCount] = useState(50);
  const [contextSnapshotError, setContextSnapshotError] = useState<string | null>(null);

  const loadThreadSnapshot = async () => {
    if (!threadId) {
      throw new Error("Thread ID is required for snapshot exports.");
    }
    if (threadSnapshot) {
      return threadSnapshot;
    }
    setIsSnapshotLoading(true);
    try {
      const snapshot = await fetchThreadSnapshot(threadId);
      setThreadSnapshot(snapshot);
      return snapshot;
    } finally {
      setIsSnapshotLoading(false);
    }
  };

  const {
    callback: handleSnapshotCopy,
  } = useAsyncCallback(async () => {
    if (!threadDetail) {
      return;
    }
    try {
      const snapshot = await loadThreadSnapshot();
      await navigator.clipboard.writeText(snapshot);
      onNoticeChange("Thread snapshot copied to clipboard.");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to copy thread snapshot.";
      setThreadDetailError(message);
    }
  });

  const {
    pending: downloadingSnapshot,
    callback: handleSnapshotDownload,
  } = useAsyncCallback(async () => {
    if (!threadDetail) {
      return;
    }
    await loadThreadSnapshot()
      .then((snapshot) => {
        const blob = new Blob([snapshot], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const safeSubject = (threadDetail.subject ?? "thread")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        link.href = url;
        link.download = `${safeSubject || "thread"}-snapshot.md`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        onNoticeChange("Thread snapshot downloaded.");
        setIsExportOpen(false);
      })
      .catch((err) => {
      const message =
        err instanceof Error ? err.message : "Unable to download thread snapshot.";
      setThreadDetailError(message);
    });
  });

  const {
    callback: handleContextSnapshotCopy,
  } = useAsyncCallback(async () => {
    if (!contextSnapshot) {
      return;
    }
    setContextSnapshotError(null);
    try {
      const formatted = formatContextSnapshotClipboard(contextSnapshot);
      await navigator.clipboard.writeText(formatted);
      onNoticeChange("AI context snapshot copied to clipboard.");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to copy AI context snapshot.";
      setContextSnapshotError(message);
    }
  });

  const {
    callback: handleContextSnapshotRefresh,
  } = useAsyncCallback(async () => {
    if (!threadId) {
      return;
    }
    setIsContextSnapshotRefreshing(true);
    setContextSnapshotError(null);
    try {
      const snapshot = await refreshThreadContextSnapshot(threadId);
      setContextSnapshot(snapshot);
      onNoticeChange("AI context snapshot refreshed.");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to refresh AI context snapshot.";
      setContextSnapshotError(message);
    } finally {
      setIsContextSnapshotRefreshing(false);
    }
  });

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) {
        return;
      }
      const menu = document.getElementById("thread-export-menu");
      const button = document.getElementById("thread-export-button");
      if (menu?.contains(event.target) || button?.contains(event.target)) {
        return;
      }
      setIsExportOpen(false);
    };
    if (isExportOpen) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [isExportOpen]);

  useEffect(() => {
    if (!threadId) {
      return;
    }

    let isActive = true;
    setIsThreadDetailLoading(true);
    setThreadDetailError(null);
    getThreadDetail(threadId)
      .then((data) => {
        if (isActive) {
          setThreadDetail(data);
        }
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load this conversation.";
        setThreadDetailError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsThreadDetailLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [threadId]);

  useEffect(() => {
    setThreadSnapshot(null);
  }, [threadId]);

  useEffect(() => {
    setContextSnapshot(threadDetail?.aiContextSnapshot ?? null);
  }, [threadDetail?.aiContextSnapshot]);

  useEffect(() => {
    if (!threadId) {
      setContextSnapshot(null);
      return;
    }

    let isActive = true;
    setIsContextSnapshotLoading(true);
    setContextSnapshotError(null);
    getThreadContextSnapshot(threadId)
      .then((snapshot) => {
        if (!isActive) {
          return;
        }
        setContextSnapshot(snapshot);
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load AI context snapshot.";
        setContextSnapshotError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsContextSnapshotLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [threadId]);

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/inbox")}>
              Inbox
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Conversation</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  const {
    pending: proposeThreadWorkPending,
    callback: proposeThreadWork,
  } = useAsyncCallback(async () => {
    if (!threadDetail) {
      return;
    }
    try {
      await proposeWorkItem(threadDetail.id);
      onNoticeChange("Work item proposed. Review it in the queue.");
      navigate("/review");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to propose work item.";
      setThreadDetailError(message);
    }
  });
  const {
    pending: copySnapshotAndClosePending,
    callback: copySnapshotAndClose,
  } = useAsyncCallback(async () => {
    await handleSnapshotCopy();
    setIsExportOpen(false);
  });
  const {
    pending: copyActionsPending,
    callback: copyActions,
  } = useAsyncCallback(async () => {
    try {
      if (!threadDetail) return;
      await navigator.clipboard.writeText(
        threadDetail.actionItems.join("\n")
      );
      onNoticeChange("Action items copied to clipboard.");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to copy action items.";
      setThreadDetailError(message);
    }
  });
  const {
    pending: createActionWorkPending,
    activeArguments: actionArguments,
    callback: createActionWork,
  } = useAsyncCallback(async (actionItem: string) => {
    if (!threadDetail) {
      return;
    }
    setActionItemBusy(actionItem);
    try {
      await createEntity({
        type: "task",
        title: actionItem,
        description: `Thread summary: ${threadDetail.summary}\n\nAction item: ${actionItem}`,
        priority: "medium",
        status: "proposed",
        threadId: threadDetail.id,
      });
      onNoticeChange(
        "Work item created from action item. Review it in the queue."
      );
      navigate("/review");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to create work item.";
      setThreadDetailError(message);
    } finally {
      setActionItemBusy(null);
    }
  });

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Conversation Detail"
        title={threadDetail?.subject || "Untitled conversation"}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/inbox")}>
              Back to inbox
            </Button>
            <Button loading={proposeThreadWorkPending} loadingLabel="Proposing work…"
              onClick={() => void proposeThreadWork()}
            >
              Propose work item
            </Button>
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setIsExportOpen((open) => !open)}
                aria-expanded={isExportOpen}
                aria-haspopup="menu"
                disabled={!threadDetail}
                id="thread-export-button"
              >
                Export
              </Button>
              {isExportOpen ? (
                <div
                  role="menu"
                  id="thread-export-menu"
                  className="absolute right-0 z-10 mt-2 w-48 rounded-md border border-border bg-popover p-2 text-sm text-popover-foreground shadow-lg"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    role="menuitem"
                    className="w-full rounded px-2 py-1 text-left hover:bg-muted"
                    loading={copySnapshotAndClosePending} loadingLabel="Copying snapshot…"
                    onClick={() => void copySnapshotAndClose()}
                    disabled={!threadDetail || isSnapshotLoading}
                  >
                    Copy snapshot
                  </Button>
                  <Button loading={downloadingSnapshot} loadingLabel="Preparing download…" variant="ghost"
                    type="button"
                    role="menuitem"
                    className="w-full rounded px-2 py-1 text-left hover:bg-muted"
                    onClick={() => {
                      void handleSnapshotDownload();
                    }}
                    disabled={!threadDetail || isSnapshotLoading}
                  >
                    Download snapshot
                  </Button>
                </div>
              ) : null}
            </div>
          </>
        }
      />

      <section className="mt-8 space-y-4">
        {isThreadDetailLoading ? (
          <div className="space-y-4">
            <Card className="border-border bg-card/60">
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-4 w-3/4" />
                <div className="flex flex-wrap gap-2">
                  <Skeleton className="h-8 w-28" />
                  <Skeleton className="h-8 w-28" />
                  <Skeleton className="h-8 w-28" />
                </div>
              </CardHeader>
            </Card>
            <Card className="border-border bg-card/60">
              <CardContent className="space-y-3 py-4">
                <Skeleton className="h-4 w-32" />
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton
                    key={`thread-msg-skeleton-${index}`}
                    className="h-4 w-full"
                  />
                ))}
              </CardContent>
            </Card>
          </div>
        ) : threadDetailError ? (
          <p className="text-sm text-red-400" role="alert">
            {threadDetailError}
          </p>
        ) : threadDetail ? (
          <>
            <Card className="border-border bg-card/60">
              <CardHeader className="space-y-2">
                <CardTitle className="text-lg">Summary & Actions</CardTitle>
                {threadDetail.customer ? (
                  <button
                    type="button"
                    className="text-left text-xs text-muted-foreground hover:text-foreground/90"
                    onClick={() =>
                      navigate(`/customers/${threadDetail.customer?.id}`)
                    }
                  >
                    Customer:{" "}
                    {threadDetail.customer.name ??
                      threadDetail.customer.email ??
                      "Unknown customer"}
                  </button>
                ) : (
                  <p className="text-xs text-muted-foreground/70">Customer: Unassigned</p>
                )}
                {threadDetail.triageRule ? (
                  <p className="text-xs text-muted-foreground">
                    Triage rule: {threadDetail.triageRule.name}
                    {threadDetail.triageOwner
                      ? ` · Owner: ${threadDetail.triageOwner.type} ${threadDetail.triageOwner.id}`
                      : ""}
                  </p>
                ) : null}
                <p className="text-sm text-muted-foreground">
                  {threadDetail.summary}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button loading={copyActionsPending} loadingLabel="Copying actions…"
                    variant="outline"
                    disabled={threadDetail.actionItems.length === 0}
                    onClick={() => void copyActions()}
                  >
                    Copy action items
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {threadDetail.actionItems.length > 0 ? (
                  threadDetail.actionItems.map((item) => (
                    <div
                      key={item}
                      className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-3 md:flex-row md:items-center md:justify-between"
                    >
                      <p className="text-sm text-foreground/90">{item}</p>
                      <Button loading={createActionWorkPending && actionArguments?.[0] === item} disabled={createActionWorkPending} loadingLabel="Creating work…"
                        variant="outline"

                        onClick={() => void createActionWork(item)}
                      >
                        {actionItemBusy === item ? "Creating..." : "Create work item"}
                      </Button>
                    </div>
                  ))
                ) : (
                  <Empty className="border-border/60 bg-muted/40">
                    <EmptyHeader>
                      <EmptyTitle>No action items yet</EmptyTitle>
                      <EmptyDescription className="text-muted-foreground">
                        Action items will appear once the summary updates.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                )}
              </CardContent>
            </Card>
            <AiContextSnapshotPanel
              snapshot={contextSnapshot}
              isLoading={isContextSnapshotLoading}
              error={contextSnapshotError}
              isRefreshing={isContextSnapshotRefreshing}
              onRefresh={handleContextSnapshotRefresh}
              onCopy={contextSnapshot ? handleContextSnapshotCopy : undefined}
              refreshDisabled={!threadId}
              notice={
                contextSnapshot?.guardrailWarnings.length
                  ? "Snapshot includes guardrail warnings."
                  : null
              }
            />
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Messages</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {threadDetail.messages.slice(-visibleMessageCount).map((message, index) => (
                  <div key={`${message.author}-${index}`} className="space-y-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">
                      {message.author}
                    </p>
                    <p className="text-sm text-foreground/90">{message.body}</p>
                  </div>
                ))}
                {threadDetail.messages.length > visibleMessageCount ? (
                  <Button
                    variant="outline"
                    onClick={() => setVisibleMessageCount((count) => count + 50)}
                  >
                    Show earlier messages (
                    {threadDetail.messages.length - visibleMessageCount})
                  </Button>
                ) : null}
              </CardContent>
            </Card>
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {threadDetail.timeline.slice(0, visibleThreadTimelineCount).map((event) => (
                  <div key={event.id} className="text-sm text-muted-foreground">
                    <span className="text-muted-foreground">
                      {new Date(event.createdAt).toLocaleString()} -{" "}
                    </span>
                    {event.summary}
                  </div>
                ))}
                {threadDetail.timeline.length > visibleThreadTimelineCount ? (
                  <Button
                    variant="outline"
                    onClick={() =>
                      setVisibleThreadTimelineCount((count) => count + 50)
                    }
                  >
                    Show earlier events (
                    {threadDetail.timeline.length - visibleThreadTimelineCount})
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          </>
        ) : null}
      </section>
    </PageShell>
  );
}
